import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { widgetActionSchema } from '@inference.engine/widget-html/web';
import { Badge } from '@monksflow/monks-ui';
import type { WidgetDefinition } from '@/entities/widget-definition';
import { declaredActions, fillPrompt, promptParts } from '../../lib/declared-actions';

const actionType = widgetActionSchema.shape.type;
const isKnownType = (type: string): type is z.infer<typeof actionType> =>
  actionType.safeParse(type).success;

/** What each of the draft's buttons does, and the message it would send with the first sample. */
export function WidgetActions({ widget }: { widget: WidgetDefinition }) {
  const { t } = useTranslation('agentBuilder');
  const actions = declaredActions(widget.draft?.document ?? '');
  const sample = widget.draft?.samples[0];

  if (actions.length === 0) {
    return <p className="m-0 text-sm text-muted-foreground">{t('widgets.actions.none')}</p>;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-sm p-0 text-sm">
      {actions.map((action) => (
        <li
          key={action.name}
          className="flex flex-col gap-xs rounded-md border border-border-light p-sm"
        >
          <div className="flex items-center gap-sm">
            <span className="min-w-0 flex-1 truncate font-medium">{action.label}</span>
            <Badge variant="secondary">
              {isKnownType(action.type) ? t(`widgets.actions.type.${action.type}`) : action.type}
            </Badge>
          </div>
          {action.prompt && (
            <p className="m-0 text-muted-foreground">
              {promptParts(action.prompt).map((part, index) =>
                'placeholder' in part ? (
                  <mark
                    key={index}
                    className="rounded-sm bg-badge-blue px-0.5 text-badge-blue-foreground"
                  >
                    {`{{${part.placeholder}}}`}
                  </mark>
                ) : (
                  <span key={index}>{part.text}</span>
                ),
              )}
            </p>
          )}
          {action.prompt && sample && (
            <p className="m-0">
              {t('widgets.actions.filled', {
                sample: sample.name,
                message: fillPrompt(action.prompt, sample.data),
              })}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
